export enum Analytic {
    CPPOpt = "cppopt",
    CPPOpt_FLEX = "cppopt_flex",
    MAPOpt = "mapopt",
    MAPOpt_FLEX = "mapopt_flex",
    PRx = "prx",
    COx = "cox",
    ROLLING_AVERAGE = "rolling_average",
    AUTO_SD_DETECTION = "auto_sd_detection",
    RESAMPLE = "resample",
    QEEG_DSA = "qeeg_dsa"
}


export const getAnalyticDisplayName = (analytic: Analytic | undefined) => {
    switch (analytic) {
        case Analytic.PRx: return "PRx"
        case Analytic.COx: return "COx"
        case Analytic.QEEG_DSA: return "qEEG DSA"
        case Analytic.CPPOpt: 
        case Analytic.CPPOpt_FLEX:
            return "CPPOpt"
        case Analytic.MAPOpt: 
        case Analytic.MAPOpt_FLEX:
            return "MAPOpt"
        case Analytic.ROLLING_AVERAGE: return "Rolling average"
        default: return ""
    }
}

type CPPOptArgs = {
    ABP: string
    ICP: string
    prxThreshold: number
}

type MAPOptArgs = {
    ABP: string
    stO2Left: string
    stO2Right: string
    enableLeft: boolean
    enableRight: boolean
    coxThreshold: number
}


export type QEEGDSADisplayScale = "power" | "voltage" | "sqrt_voltage"
export type QEEGDSAChannelGroupId =
    | "LeftHemisphere"
    | "RightHemisphere"
    | "Fp1-F7"
    | "F7-T7"
    | "T7-P7"
    | "P7-O1"
    | "Fp1-F3"
    | "F3-C3"
    | "C3-P3"
    | "P3-O1"
    | "Fp2-F8"
    | "F8-T8"
    | "T8-P8"
    | "P8-O2"
    | "Fp2-F4"
    | "F4-C4"
    | "C4-P4"
    | "P4-O2"
    | "Fz-Cz"
    | "Cz-Pz"

export type QEEGDSAChannelGroup = {
    id: QEEGDSAChannelGroupId
    name: string
}

export const DEFAULT_QEEG_DSA_CHANNEL_GROUP_ID: QEEGDSAChannelGroupId = "LeftHemisphere"

export const QEEG_DSA_CHANNEL_GROUPS: QEEGDSAChannelGroup[] = [
    { id: "LeftHemisphere", name: "Left Hemisphere" },
    { id: "RightHemisphere", name: "Right Hemisphere" },
    { id: "Fp1-F7", name: "Fp1-F7" },
    { id: "F7-T7", name: "F7-T7" },
    { id: "T7-P7", name: "T7-P7" },
    { id: "P7-O1", name: "P7-O1" },
    { id: "Fp1-F3", name: "Fp1-F3" },
    { id: "F3-C3", name: "F3-C3" },
    { id: "C3-P3", name: "C3-P3" },
    { id: "P3-O1", name: "P3-O1" },
    { id: "Fp2-F8", name: "Fp2-F8" },
    { id: "F8-T8", name: "F8-T8" },
    { id: "T8-P8", name: "T8-P8" },
    { id: "P8-O2", name: "P8-O2" },
    { id: "Fp2-F4", name: "Fp2-F4" },
    { id: "F4-C4", name: "F4-C4" },
    { id: "C4-P4", name: "C4-P4" },
    { id: "P4-O2", name: "P4-O2" },
    { id: "Fz-Cz", name: "Fz-Cz" },
    { id: "Cz-Pz", name: "Cz-Pz" },
]

export const getQEEGDSAChannelGroupName = (channelGroupId: QEEGDSAChannelGroupId): string => {
    return QEEG_DSA_CHANNEL_GROUPS.find(channelGroup => channelGroup.id === channelGroupId)?.name ?? channelGroupId
}

export const getQEEGDSATraceName = (channelGroupId: QEEGDSAChannelGroupId): string => {
    return `${getQEEGDSAChannelGroupName(channelGroupId)} DSA`
}

export type QEEGDSAAnalysis = {
    analytic: Analytic.QEEG_DSA
    calculationPeriodMs: number
    calculationWindowMs: number
    channelGroupId: QEEGDSAChannelGroupId
    displayScale: QEEGDSADisplayScale
    stft: {
        windowSec: number
        overlap: number
        minFreqHz: number
        maxFreqHz: number
        filters: {
            lffHz: number | null
            hffHz: number | null
            notchHz: number | null
        }
    }
    hzStep: number
    epochSec: number
}


// On-demand analytics are calculated along with the page request at request time.
export type TimedOnDemandAnalysis = {
    calculationPeriodMs: number // How often we should calculate the analytic
    calculationWindowMs: number // How much data we need for the analytic
    analytic: Analytic // The analytic that we are calculating
}


export type OnDemandAnalysis = TimedOnDemandAnalysis | QEEGDSAAnalysis

export type OnDemandAnalysisRequest = TimedOnDemandAnalysis | Omit<QEEGDSAAnalysis, "calculationPeriodMs" | "calculationWindowMs">

export const getQEEGDSARequestAnalysis = (analysis: QEEGDSAAnalysis): Omit<QEEGDSAAnalysis, "calculationPeriodMs" | "calculationWindowMs"> => {
    const { calculationPeriodMs, calculationWindowMs, ...requestAnalysis } = analysis
    return requestAnalysis
}


export type PRxAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.PRx
    ABP: string
    ICP: string
}


export type COxAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.COx
    StO2Left: string
    StO2Right: string
    enableLeft: boolean
    enableRight: boolean
    ABP: string
}


export type CPPOptAnalysis = TimedOnDemandAnalysis & CPPOptArgs & {
    analytic: Analytic.CPPOpt
}


export type CPPOptFlexAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.CPPOpt_FLEX
    ABP: string
    ICP: string
    prxThreshold: number
}


export type MAPOptAnalysis = TimedOnDemandAnalysis & MAPOptArgs & {
    analytic: Analytic.MAPOpt
}


export type MAPOptFlexAnalysis = TimedOnDemandAnalysis & MAPOptArgs & {
    analytic: Analytic.MAPOpt_FLEX
}


export type AutoSDAnalysis = TimedOnDemandAnalysis & {
    analytic: Analytic.AUTO_SD_DETECTION
    channels: string[]
}


export type GenericOnDemandAnalysis = TimedOnDemandAnalysis & {
    rawDataKey: string
}


export const isGenericAnalytic = (analytic: Analytic | undefined) => {
    return analytic && new Set([
        Analytic.ROLLING_AVERAGE,
    ]).has(analytic)
}
